import React, { useState } from "react";
import { Box, Flex, HStack, Text } from "@chakra-ui/react";
import { c } from "../classroomColors";
import { chartColors, blockEdge, mutedColors, STATE_ORDER, STAGES, BANDS, DOT_KEY } from "./markingTokens";

/** Identity is never colour alone: every chart ships this. */
export const StateLegend = ({ isDark, counts = null }) => {
  const cc = chartColors(isDark);
  const edge = blockEdge(isDark);
  return (
    <HStack spacing="14px" wrap="wrap" rowGap="6px">
      {STATE_ORDER.map((k) => (
        <HStack key={k} spacing="6px">
          <Box boxSize="9px" borderRadius="3px" bg={cc[DOT_KEY[k]]} border="1px solid" borderColor={edge[DOT_KEY[k]]} flexShrink={0} />
          <Text fontSize="11.5px" color={c.textSub}>
            {STAGES[k].label}
            {counts && (
              <Box as="span" color={c.textMuted} ml="4px" sx={{ fontVariantNumeric: "tabular-nums" }}>
                {counts[k]}
              </Box>
            )}
          </Text>
        </HStack>
      ))}
    </HStack>
  );
};

const Tip = ({ x, y, children }) => (
  <Box
    position="absolute"
    left={`${x}px`}
    top={`${y}px`}
    transform="translate(-50%, -110%)"
    bg={c.text}
    color={c.surface}
    px="9px"
    py="6px"
    borderRadius="7px"
    fontSize="11.5px"
    fontWeight="400"
    whiteSpace="nowrap"
    pointerEvents="none"
    zIndex={3}
    boxShadow="0 6px 18px -6px rgba(0,0,0,0.4)"
  >
    {children}
  </Box>
);

/**
 * Where a set of things stands, one column each — assignments inside a
 * classroom, classrooms on the dashboard.
 *
 * A column's height is how much work it holds, and its blocks are the four
 * states, so scale and composition read together. Everything recedes except
 * the one most in need of marking, which stays at full strength with its
 * count called out; hovering brings any other column forward. Exact numbers
 * are in the tooltip, so the palest block never carries meaning on contrast
 * alone.
 *
 * @param items [{ key, name, counts, total, submitted, needsReview }]
 */
export const StateColumns = ({ items: assignments, isDark, onSelect, plotHeight = 210 }) => {
  const cc = chartColors(isDark);
  const edge = blockEdge(isDark);
  const muted = mutedColors(isDark);
  const [tip, setTip] = useState(null);
  const [hovered, setHovered] = useState(null);

  const maxTotal = Math.max(1, ...assignments.map((a) => a.total));
  // The one that needs marking most; falls back to the first when none do.
  const focusKey = (assignments.find((a) => a.needsReview > 0) || assignments[0])?.key;
  const active = hovered || focusKey;

  const PLOT = plotHeight; // tallest column
  const MIN_BLOCK = 13;  // a single student still has to be visible

  return (
    <Box position="relative" data-chart-host>
      <Flex
        align="flex-end"
        justify="flex-start"
        gap={{ base: "14px", md: "24px" }}
        pt="26px"
        pb="4px"
        overflowX="auto"
        sx={{ scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}
      >
        {assignments.map((a) => {
          const isActive = a.key === active;
          // Bottom-up: finished work settles at the base, outstanding floats.
          const segs = [...STATE_ORDER]
            .reverse()
            .map((k) => ({ k, n: a.counts[k] }))
            .filter((s) => s.n > 0);
          const colHeight = Math.max(MIN_BLOCK, (a.total / maxTotal) * PLOT);
          const unit = (colHeight - (segs.length - 1) * 3) / Math.max(1, a.total);

          return (
            <Flex
              key={a.key}
              as="button"
              onClick={() => onSelect(a)}
              direction="column"
              align="center"
              flex="1 1 0"
              minW="74px"
              maxW="132px"
              onMouseEnter={() => setHovered(a.key)}
              onMouseLeave={() => { setHovered(null); setTip(null); }}
              position="relative"
            >
              {/* the callout, as on the reference */}
              <Box height="24px" mb="6px" position="relative" width="100%">
                {isActive && a.needsReview > 0 && (
                  <Box
                    position="absolute"
                    left="50%"
                    transform="translateX(-50%)"
                    px="9px"
                    py="3px"
                    borderRadius="full"
                    bg={c.surface}
                    border="1px solid"
                    borderColor={c.border}
                    boxShadow="0 4px 12px -4px rgba(0,0,0,0.18)"
                    fontSize="11px"
                    fontWeight="500"
                    color={c.text}
                    whiteSpace="nowrap"
                  >
                    {a.needsReview} to review
                  </Box>
                )}
              </Box>

              <Flex
                direction="column-reverse"
                justify="flex-start"
                gap="3px"
                width="100%"
                height={`${colHeight}px`}
                transition="opacity 0.18s"
              >
                {segs.map((seg) => (
                  <Box
                    key={seg.k}
                    width="100%"
                    height={`${Math.max(MIN_BLOCK, seg.n * unit)}px`}
                    borderRadius="7px"
                    bg={isActive ? cc[seg.k] : muted[seg.k]}
                    border="1px solid"
                    borderColor={isActive ? edge[seg.k] : muted.edge}
                    transition="background 0.18s, border-color 0.18s"
                    onMouseEnter={(e) => {
                      const r = e.currentTarget.getBoundingClientRect();
                      const host = e.currentTarget.closest("[data-chart-host]")?.getBoundingClientRect();
                      if (!host) return;
                      setTip({
                        x: r.left - host.left + r.width / 2,
                        y: r.top - host.top,
                        text: `${seg.n} ${STAGES[seg.k].label.toLowerCase()}`,
                      });
                    }}
                  />
                ))}
              </Flex>

              <Text
                mt="10px"
                fontSize="11.5px"
                fontWeight={isActive ? "500" : "400"}
                color={isActive ? c.text : c.textMuted}
                noOfLines={2}
                textAlign="center"
                lineHeight="1.3"
                title={a.name}
                transition="color 0.18s"
              >
                {a.name}
              </Text>
              <Text fontSize="10.5px" color={c.textMuted} mt="2px"
                sx={{ fontVariantNumeric: "tabular-nums" }}>
                {a.submitted}/{a.total} in
              </Text>
            </Flex>
          );
        })}
      </Flex>
      {tip && <Tip x={tip.x} y={tip.y}>{tip.text}</Tip>}
    </Box>
  );
};

/**
 * How the class did on one assignment. Magnitude, so one hue light→dark; the
 * band a mark falls in is the only thing being compared.
 */
export const MarkDistribution = ({ counts, isDark, average }) => {
  const cc = chartColors(isDark);
  const [tip, setTip] = useState(null);
  const max = Math.max(1, ...counts);
  const marked = counts.reduce((n, k) => n + k, 0);

  if (!marked) {
    return (
      <Flex align="center" justify="center" height="150px">
        <Text fontSize="13px" color={c.textMuted} fontWeight="300">
          No marks yet — the spread will show here once work is marked.
        </Text>
      </Flex>
    );
  }

  // One hue, more-is-darker, from the brand blue's chart step.
  const shades = isDark
    ? ["#1d5f75", "#227a95", "#2a9cc2", "#54b4d4", "#7fc9e2"]
    : ["#cbe9f3", "#95d2e5", "#4fadcc", "#1f93b8", "#0b86ad"];

  return (
    <Box position="relative" data-chart-host>
      <Flex align="flex-end" gap="10px" height="150px" px="2px">
        {counts.map((n, i) => {
          const h = (n / max) * 118;
          return (
            <Flex key={BANDS[i].label} direction="column" align="center" flex="1" gap="7px">
              <Text fontSize="11.5px" color={n ? c.text : c.textMuted} fontWeight={n ? "500" : "300"}
                sx={{ fontVariantNumeric: "tabular-nums" }}>
                {n || ""}
              </Text>
              <Box
                width="100%"
                maxW="46px"
                height={`${Math.max(n ? 5 : 2, h)}px`}
                bg={n ? shades[i] : c.track}
                borderTopRadius="4px"
                transition="opacity 0.15s"
                _hover={{ opacity: 0.85 }}
                onMouseEnter={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  const host = e.currentTarget.closest("[data-chart-host]")?.getBoundingClientRect();
                  if (!host) return;
                  setTip({
                    x: r.left - host.left + r.width / 2,
                    y: r.top - host.top,
                    text: `${n} student${n === 1 ? "" : "s"} · ${BANDS[i].label}%`,
                  });
                }}
                onMouseLeave={() => setTip(null)}
              />
            </Flex>
          );
        })}
      </Flex>
      <Flex gap="10px" mt="7px" pt="7px" borderTop="1px solid" borderColor={cc.grid}>
        {BANDS.map((b) => (
          <Text key={b.label} flex="1" textAlign="center" fontSize="11px" color={c.textMuted}>
            {b.label}
          </Text>
        ))}
      </Flex>
      {average != null && (
        <Text fontSize="11.5px" color={c.textMuted} mt="8px" textAlign="center">
          Class average {Math.round(average)}% across {marked} marked
        </Text>
      )}
      {tip && <Tip x={tip.x} y={tip.y}>{tip.text}</Tip>}
    </Box>
  );
};
